// Evidence / check helpers shared by Domain 01 units.
//
// Evidence rules receive a runtime event:
//   { type:'cmd', device, key, args, result, ok, output, mode, stage }
//   { type:'action', name, ... }
// IOS event keys are the matched command pattern (e.g. 'show ip interface brief').
// Windows keys are the tool name (e.g. 'ping', 'ipconfig /all').

import { roundTrip, dnsQuery, ifStatus, hostIp, linkState, linkOf } from '../../engine/net/sim.js';
import { clone } from '../../engine/net/model.js';
import { macCisco } from '../../engine/net/addr.js';
import { WinShell } from '../../engine/pc/winshell.js';

const keyIs = (ev, k) => (Array.isArray(k) ? k.includes(ev.key) : k instanceof RegExp ? k.test(ev.key) : ev.key === k);

/** Command `key` was run on `device` (optionally output must match). */
export const ran = (device, key, outRe) => (ev) =>
  ev.type === 'cmd' && ev.device === device && keyIs(ev, key) && ev.ok !== false && (!outRe || outRe.test(ev.output));

/** Windows ping from `device` to `target` (IP or name); success=true requires all echoes. */
export const pinged = (device, target, { success = null, all = false } = {}) => (ev) => {
  if (ev.type !== 'cmd' || ev.device !== device || ev.key !== 'ping') return false;
  const t = String(ev.args?.target || '').toLowerCase();
  if (target && t !== String(target).toLowerCase() && ev.args?.ip !== target) return false;
  if (success === null) return true;
  if (success === false) return !ev.result?.success;
  return all ? !!ev.result?.allOk : !!ev.result?.success;
};
/** IOS ping from a router/switch. */
export const iosPinged = (device, target, success = true) => (ev) =>
  ev.type === 'cmd' && ev.device === device && ev.key === 'ping <HOST>' && ev.args?.[0] === target
  && (success === null || (success ? /Success rate is [1-9]/.test(ev.output) : /Success rate is 0/.test(ev.output)));

export const resolved = (device, name, success = true) => (ev) =>
  ev.type === 'cmd' && ev.device === device && ev.key === 'nslookup' && (!name || String(ev.args?.name).toLowerCase() === name) && !!ev.result?.success === success;
export const fetched = (device, host, success = true) => (ev) =>
  ev.type === 'cmd' && ev.device === device && (ev.key === 'curl') && (!host || String(ev.args?.host).toLowerCase() === host) && !!ev.result?.success === success;
export const tnc = (device, port, success) => (ev) =>
  ev.type === 'cmd' && ev.device === device && ev.key === 'Test-NetConnection' && (port == null || ev.args?.port === port) && (success == null || !!ev.result?.success === success);
export const action = (name, pred = () => true) => (ev) => ev.type === 'action' && ev.name === name && pred(ev);
export const any = (...rules) => (ev, rt) => rules.some((r) => r(ev, rt));

// ---------------------------------------------------------------- checks (no side effects)
export const reach = (rt, src, dst, opts) => roundTrip(clone(rt.net), src, dst, opts).ok;
export const reachClean = (rt, src, dst) => { const r = roundTrip(clone(rt.net), src, dst); return r.ok && !r.lossy; };
export const resolves = (rt, src, name) => dnsQuery(clone(rt.net), src, name).ok;
/** Full user experience: name resolves and TCP/80 answers. */
export function webWorks(rt, src, host = 'www.example.com') {
  const n = clone(rt.net);
  const q = dnsQuery(n, src, host);
  if (!q.ok) return false;
  return roundTrip(n, src, q.address, { proto: 'tcp', port: 80 }).ok;
}
export const up = (rt, d, i) => ifStatus(rt.net, d, i).protocol === 'up';
export const linkClean = (rt, d, i) => linkState(rt.net, linkOf(rt.net, d, i)) === 'up';
export const hostCfg = (rt, d) => hostIp(rt.net.devices[d]);

// ---------------------------------------------------------------- value matching for observe widgets
export const norm = (s) => String(s).trim().toLowerCase();
export const normMac = (s) => String(s).toLowerCase().replace(/[^0-9a-f]/g, '');
export const sameMac = (a, b) => normMac(a).length === 12 && normMac(a) === normMac(b);
export const normIf = (s) => norm(s).replace(/\s+/g, '').replace(/^fastethernet/, 'fa').replace(/^gigabitethernet/, 'gi').replace(/^f(?=\d)/, 'fa').replace(/^g(?=\d)/, 'gi');
export const yes = (s) => /^(y|yes|true)$/i.test(String(s).trim());
export const no = (s) => /^(n|no|false|none)$/i.test(String(s).trim());
export { macCisco, WinShell };

/** Config-change log for documentation (IOS config-mode lines + Windows changes). */
export function changeLog(rt) {
  return rt.state.log.filter((e) =>
    (e.type === 'cmd' && e.mode && String(e.mode).startsWith('config') && e.ok && !/^(end|exit|do )/.test(e.line))
    || (e.type === 'cmd' && /^netsh (set|set address|set dns|set interface)/.test(e.key) && e.ok)
    || (e.type === 'action' && ['adapter', 'reseat', 'replace-cable'].includes(e.name)))
    .map((e) => (e.type === 'cmd' ? `${e.device}: ${e.line}` : `${e.device || e.link}: ${e.name}${e.ip ? ` ${e.ip} ${e.mask} gw ${e.gw || '-'} dns ${e.dns?.[0] || '-'}` : ''}`));
}
